import { supabase } from '../supabaseClient';

const SUPABASE_URL = supabase.supabaseUrl || import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = supabase.supabaseKey || import.meta.env.VITE_SUPABASE_ANON_KEY;

// 단체 통화 인증 토큰 캐시 (창 닫힘 keepalive 전송용)
let cachedAccessToken = null;
let authWatchOff = null;

function extractErrorReason(err) {
  const msg = String(err?.message || err || '');
  if (msg.includes('call_full')) return 'call_full';
  if (msg.includes('call_not_active')) return 'call_not_active';
  if (msg.includes('not_self')) return 'not_self';
  return 'error';
}

/**
 * 단체 통화 걸기 (DB 창구 start_group_call)
 * - 같은 방에 살아 있는 단체 통화가 있으면 새로 만들지 않고 그 통화 번호를 돌려준다 (created: false)
 * - 새로 만들면 통화 기록과 건 사람 참가 기록이 한 번에 생긴다 (created: true)
 * - 실패 사유(reason): not_authenticated / invalid_kind / not_group / not_member / too_many_calls / invalid_arguments / error
 */
export async function startGroupCall(conversationId, kind = 'audio') {
  const fail = (reason, error = null) => ({ ok: false, reason, error, callId: null, created: false, kind: null });
  if (!conversationId) {
    console.warn('[단체 통화 DB] startGroupCall 실패 — 사유: invalid_arguments');
    return fail('invalid_arguments', 'conversationId is missing');
  }
  try {
    const { data, error } = await supabase.rpc('start_group_call', {
      p_conversation_id: conversationId,
      p_kind: kind,
    });
    if (error) {
      console.warn(`[단체 통화 DB] startGroupCall 실패 — 사유: error, 원문: ${error.message}`);
      return fail('error', error.message);
    }
    const result = data?.result;
    if ((result === 'created' || result === 'existing') && data?.call_id) {
      return {
        ok: true,
        reason: null,
        error: null,
        callId: data.call_id,
        created: result === 'created',
        kind: data.kind === 'video' ? 'video' : 'audio',
      };
    }
    console.warn(`[단체 통화 DB] startGroupCall 실패 — 사유: ${result || 'error'}`);
    return fail(result || 'error');
  } catch (e) {
    console.warn(`[단체 통화 DB] startGroupCall 실패 — 사유: error, 원문: ${e?.message || e}`);
    return fail('error', e?.message || String(e));
  }
}

/**
 * 단체 통화 입장 (DB 창구 join_group_call)
 * - 4인 상한과 동시 나가기 겹침은 DB가 통화 기록을 잠그고 판정한다
 * - 건 사람이 아닌 첫 입장자가 들어올 때만 '울리는 중' → '통화 중'
 * - 1분 넘게 울리던(건 사람 폰 꺼짐) 통화는 DB가 '실패'로 닫고 call_not_active 를 돌려준다
 * - 실패 사유(reason): not_authenticated / call_not_found / not_group / not_member / call_not_active / call_full / invalid_arguments / error
 */
export async function joinGroupCall(callId) {
  if (!callId) {
    console.warn('[단체 통화 DB] joinGroupCall 실패 — 사유: invalid_arguments');
    return { ok: false, reason: 'invalid_arguments', error: 'callId is missing', alreadyJoined: false };
  }
  try {
    const { data, error } = await supabase.rpc('join_group_call', { p_call_id: callId });
    if (error) {
      console.warn(`[단체 통화 DB] joinGroupCall 실패 — 사유: error, 원문: ${error.message}`);
      return { ok: false, reason: 'error', error: error.message, alreadyJoined: false };
    }
    if (data === 'joined' || data === 'already_joined') {
      return { ok: true, reason: null, error: null, alreadyJoined: data === 'already_joined' };
    }
    console.warn(`[단체 통화 DB] joinGroupCall 실패 — 사유: ${data || 'error'}`);
    return { ok: false, reason: data || 'error', error: null, alreadyJoined: false };
  } catch (e) {
    console.warn(`[단체 통화 DB] joinGroupCall 실패 — 사유: error, 원문: ${e?.message || e}`);
    return { ok: false, reason: 'error', error: e?.message || String(e), alreadyJoined: false };
  }
}

/**
 * 단체 통화 나가기 처리
 * - call_participants 본인 행만 left 로 기록
 * - calls 표는 건드리지 않음
 */
export async function leaveGroupCall(callId, userId) {
  if (!callId || !userId) {
    const reason = 'invalid_arguments';
    console.warn(`[단체 통화 DB] leaveGroupCall 실패 — 사유: ${reason}, 원문: callId or userId is missing`);
    return { ok: false, reason, error: 'callId or userId is missing' };
  }
  const nowIso = new Date().toISOString();
  try {
    const { error } = await supabase
      .from('call_participants')
      .update({
        state: 'left',
        left_at: nowIso,
      })
      .eq('call_id', callId)
      .eq('profile_id', userId)
      .eq('state', 'joined');

    if (error) {
      const reason = extractErrorReason(error);
      console.warn(`[단체 통화 DB] leaveGroupCall 실패 — 사유: ${reason}, 원문: ${error.message}`);
      return { ok: false, reason, error: error.message };
    }
    return { ok: true, reason: null, error: null };
  } catch (e) {
    const reason = extractErrorReason(e);
    console.warn(`[단체 통화 DB] leaveGroupCall 실패 — 사유: ${reason}, 원문: ${e?.message || e}`);
    return { ok: false, reason, error: e?.message || String(e) };
  }
}

/**
 * 단체 통화 인증 사전 준비 (창 닫힘 keepalive용 토큰 캐시 및 감시)
 * - 통화 시작 시 호출
 * - getSession()으로 cachedAccessToken을 채우고, onAuthStateChange로 갱신 추적
 */
export async function primeGroupCallAuth() {
  try {
    const { data } = await supabase.auth.getSession();
    cachedAccessToken = data?.session?.access_token ?? null;
  } catch (e) {
    console.warn('[단체 통화 DB] primeGroupCallAuth getSession 실패:', e?.message || e);
  }

  if (!authWatchOff) {
    try {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        cachedAccessToken = session?.access_token ?? null;
      });
      authWatchOff = () => {
        try {
          subscription?.unsubscribe();
        } catch {}
      };
    } catch (e) {
      console.warn('[단체 통화 DB] primeGroupCallAuth onAuthStateChange 실패:', e?.message || e);
    }
  }
}

/**
 * 단체 통화 인증 캐시 및 감시 해제
 * - 통화 종료 시 호출
 */
export function releaseGroupCallAuth() {
  if (authWatchOff) {
    try {
      authWatchOff();
    } catch {}
    authWatchOff = null;
  }
  cachedAccessToken = null;
}

/**
 * 창 닫힘 시 keepalive fetch로 단체 통화 나가기 (call_participants state='left')
 * - 브라우저 종료·언로드 중단 방지를 위해 keepalive: true 사용
 * - 사전 캐시된 access_token과 환경 설정이 없으면 false 반환
 */
export function leaveGroupCallKeepalive(callId, userId) {
  if (!callId || !userId) return false;
  if (!cachedAccessToken || !SUPABASE_URL || !SUPABASE_ANON_KEY) return false;

  const endpoint = `${SUPABASE_URL}/rest/v1/call_participants?call_id=eq.${encodeURIComponent(callId)}&profile_id=eq.${encodeURIComponent(userId)}&state=eq.joined`;
  const body = JSON.stringify({
    state: 'left',
    left_at: new Date().toISOString(),
  });

  try {
    void fetch(endpoint, {
      method: 'PATCH',
      keepalive: true,
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${cachedAccessToken}`,
        'Content-Type': 'application/json',
        Prefer: 'return=minimal',
      },
      body,
    }).catch((e) => {
      console.warn('[단체 통화 DB] leaveGroupCallKeepalive fetch 실패:', e?.message || e);
    });
    console.log('[단체 통화 DB] leaveGroupCallKeepalive 전송 요청 완료');
    return true;
  } catch (e) {
    console.warn('[단체 통화 DB] leaveGroupCallKeepalive 예외:', e?.message || e);
    return false;
  }
}

/**
 * 단체 통화 거절 처리
 * - call_participants 본인 행만 declined 로 기록
 * - calls 표는 건드리지 않음
 */
export async function declineGroupCall(callId, userId) {
  if (!callId || !userId) {
    const reason = 'invalid_arguments';
    console.warn(`[단체 통화 DB] declineGroupCall 실패 — 사유: ${reason}, 원문: callId or userId is missing`);
    return { ok: false, reason, error: 'callId or userId is missing' };
  }
  const nowIso = new Date().toISOString();
  try {
    const { error } = await supabase
      .from('call_participants')
      .update({
        state: 'declined',
        left_at: nowIso,
      })
      .eq('call_id', callId)
      .eq('profile_id', userId)
      .eq('state', 'invited');

    if (error) {
      const reason = extractErrorReason(error);
      console.warn(`[단체 통화 DB] declineGroupCall 실패 — 사유: ${reason}, 원문: ${error.message}`);
      return { ok: false, reason, error: error.message };
    }
    return { ok: true, reason: null, error: null };
  } catch (e) {
    const reason = extractErrorReason(e);
    console.warn(`[단체 통화 DB] declineGroupCall 실패 — 사유: ${reason}, 원문: ${e?.message || e}`);
    return { ok: false, reason, error: e?.message || String(e) };
  }
}

/**
 * 단체 통화 취소 처리 (아무도 받지 않은 ringing 상태일 때만 calls 취소)
 * - 이미 누군가 들어왔으면(.eq('status', 'ringing') 불일치) 0건 변경으로 끝남
 */
export async function cancelGroupCallIfRinging(callId, status, endedBy = null) {
  if (!callId) {
    const reason = 'invalid_arguments';
    console.warn(`[단체 통화 DB] cancelGroupCallIfRinging 실패 — 사유: ${reason}, 원문: callId is missing`);
    return { ok: false, reason, error: 'callId is missing' };
  }
  const nowIso = new Date().toISOString();
  try {
    const { error } = await supabase
      .from('calls')
      .update({
        status,
        ended_at: nowIso,
        ended_by: endedBy,
      })
      .eq('id', callId)
      .eq('status', 'ringing');

    if (error) {
      const reason = extractErrorReason(error);
      console.warn(`[단체 통화 DB] cancelGroupCallIfRinging 실패 — 사유: ${reason}, 원문: ${error.message}`);
      return { ok: false, reason, error: error.message };
    }
    return { ok: true, reason: null, error: null };
  } catch (e) {
    const reason = extractErrorReason(e);
    console.warn(`[단체 통화 DB] cancelGroupCallIfRinging 실패 — 사유: ${reason}, 원문: ${e?.message || e}`);
    return { ok: false, reason, error: e?.message || String(e) };
  }
}

/**
 * 단체 통화 살아 있음 기록 (서버 시계 now()로 last_seen_at 갱신)
 * - callId 없으면 false 반환, 예외를 밖으로 던지지 않음
 */
export async function touchGroupCallPresence(callId) {
  if (!callId) return false;
  try {
    const { data, error } = await supabase.rpc('touch_group_call_presence', { p_call_id: callId });
    if (error) {
      console.warn(`[단체 통화 DB] touchGroupCallPresence 실패 — 원문: ${error.message}`);
      return false;
    }
    return data === true;
  } catch (e) {
    console.warn(`[단체 통화 DB] touchGroupCallPresence 실패 — 원문: ${e?.message || e}`);
    return false;
  }
}

/**
 * 무소식(90초 초과) 단체 통화 유령 참가자 청소
 * - callId 없으면 0 반환, 예외를 밖으로 던지지 않음
 */
export async function cleanupStaleGroupCallParticipants(callId) {
  if (!callId) return 0;
  try {
    const { data, error } = await supabase.rpc('cleanup_stale_group_call_participants', { p_call_id: callId });
    if (error) {
      console.warn(`[단체 통화 DB] cleanupStaleGroupCallParticipants 실패 — 원문: ${error.message}`);
      return 0;
    }
    return Number(data) || 0;
  } catch (e) {
    console.warn(`[단체 통화 DB] cleanupStaleGroupCallParticipants 실패 — 원문: ${e?.message || e}`);
    return 0;
  }
}

export const GROUP_PARTICIPANT_ALIVE_WINDOW_MS = 90000;

/**
 * 단체 통화 살아 있는 참가자 명단 조회 (call_participants SELECT)
 * - 내 행이 아직 joined 인지(myJoined)와 나를 제외한 최근 90초 이내 joined 참가자 id 목록(alivePeerIds) 반환
 * - PostgREST SELECT 필터는 서버 시계 함수(now())를 직접 받을 수 없으므로 클라이언트 시계(Date.now() - 90000)로 비교
 */
export async function fetchActiveGroupCallParticipants(callId, myUserId) {
  if (!callId || !myUserId) {
    return { ok: false, myJoined: false, alivePeerIds: [], error: 'invalid_arguments' };
  }
  try {
    const { data, error } = await supabase
      .from('call_participants')
      .select('profile_id, state, last_seen_at')
      .eq('call_id', callId)
      .eq('state', 'joined');

    if (error) {
      console.warn(`[단체 통화 DB] 참가자 명단 조회 실패 — 원문: ${error.message}`);
      return { ok: false, myJoined: false, alivePeerIds: [], error: error.message };
    }

    const rows = Array.isArray(data) ? data : [];
    const cutoffMs = Date.now() - GROUP_PARTICIPANT_ALIVE_WINDOW_MS;
    let myJoined = false;
    const alivePeerIds = [];

    for (const row of rows) {
      if (!row?.profile_id) continue;
      if (row.profile_id === myUserId) {
        myJoined = true;
        continue;
      }
      const seenMs = row.last_seen_at ? Date.parse(row.last_seen_at) : NaN;
      if (Number.isFinite(seenMs) && seenMs >= cutoffMs) {
        alivePeerIds.push(row.profile_id);
      }
    }

    return { ok: true, myJoined, alivePeerIds, error: null };
  } catch (e) {
    console.warn(`[단체 통화 DB] 참가자 명단 조회 예외 — 원문: ${e?.message || e}`);
    return { ok: false, myJoined: false, alivePeerIds: [], error: e?.message || String(e) };
  }
}

